import React, { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import HelpHint from "../../components/help-hint/HelpHint";
import SvgLoadingAnimation from "../../components/loading";
import InlineLoading from "../../components/loading/InlineLoading";
import Table, { TableColumn } from "../../components/table/Table";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import ConfirmModal from "../../components/modal/ConfirmModal";
import ToolTip from "../../components/tooltip/ToolTip";
import {
  useGetAdminMileageRanksQuery,
  useDeleteAdminMileageRankMutation,
  type AdminMileageRank,
} from "../../app/services/adminMileageRanks";
import MileageRankModal from "./components/MileageRankModal";

// ===== styled-components =====

// アカウント管理と同じフルワイドレイアウト。
const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap16",
})`
  padding: 24px 16px 80px;
`;

const EmptyCell = styled.td.attrs({
  className: "bodytext-13 u-text-light-gray u-text-center",
})`
  padding: 32px 8px !important;
`;

// 行クリックで編集モーダルを開く（旧 UI と同じ操作感）
const ClickableRow = styled.tr`
  cursor: pointer;

  @media (hover: hover) {
    &:hover td {
      background: var(--soremo-background);
    }
  }
`;

// カード画像サムネイル（旧 card-image-container__sm 相当、216:136 比率）
const CardThumbnail = styled.div<{ $src: string | null }>`
  width: 64px;
  aspect-ratio: 216 / 136;
  border-radius: 4px;
  border: 1px solid var(--soremo-border);
  background: ${({ $src }) =>
    $src
      ? `center / cover no-repeat url(${$src})`
      : "var(--soremo-background)"};
`;

// ===== メインコンポーネント =====

/**
 * マイレージ編集ページ（/console/mileage、ADMIN = master_admin のみ）。
 *
 * 旧 Django `/mileages/` 画面の移植。マイレージランク（ステージ）定義の
 * 一覧・作成・編集・削除を表形式で行う。
 *
 * - 全件でも最大 8 件のためページングなし。
 * - 行クリックで編集モーダル（画像は ImageEditorModal で 216:136 クロップ）。
 * - `is_default` ランクはボーダーライン変更・削除ができない。
 * - ランク分布比率は閲覧のみ（アクティブなクリエイターの所属割合）。
 */
const AdminMileageRanks: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  const role: string | undefined = currentUser?.data?.role;
  const isAdmin = role === "ADMIN";

  const { data, isLoading, isError } = useGetAdminMileageRanksQuery(
    undefined,
    { skip: !isAdmin },
  );
  const ranks = data?.data?.ranks ?? [];
  const artistTotal = data?.data?.artist_total ?? 0;
  const maxRanks = data?.data?.max_ranks ?? 8;
  const minRanks = data?.data?.min_ranks ?? 2;
  const isRankLimitReached = ranks.length >= maxRanks;
  const isMinimumReached = ranks.length <= minRanks;

  // 編集 / 新規作成モーダル（editTarget=null かつ isCreating=true で新規）
  const [editTarget, setEditTarget] = useState<AdminMileageRank | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // 削除確認モーダル
  const [deleteTarget, setDeleteTarget] = useState<AdminMileageRank | null>(
    null,
  );
  const [deleteRank, { isLoading: isDeleting }] =
    useDeleteAdminMileageRankMutation();
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteRank(deleteTarget.mileage_id).unwrap();
      toast.success(
        `ステージ「${deleteTarget.rank_name_jp || deleteTarget.rank_name_en}」を削除しました`,
      );
      setDeleteTarget(null);
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message;
      toast.error(message || "ステージの削除に失敗しました");
    }
  }, [deleteTarget, deleteRank]);

  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }

  if (!isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  const columns: TableColumn[] = [
    { key: "image", header: "", width: 80, minWidth: 80, resizable: false },
    { key: "rank_name_jp", header: "ステージ名", minWidth: 120 },
    { key: "rank_name_en", header: "ステージ名（英）", minWidth: 120 },
    {
      key: "point",
      header: "ボーダーライン",
      width: 140,
      minWidth: 120,
    },
    { key: "usage_fee", header: "利用料率", width: 100, minWidth: 88 },
    {
      key: "distribution",
      header: "ランク分布比率",
      width: 140,
      minWidth: 120,
    },
    { key: "delete", header: "", width: 48, minWidth: 48, resizable: false },
  ];

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            <InlineLoading visible />
          </EmptyCell>
        </tr>
      );
    }
    if (isError) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            マイレージステージの取得に失敗しました。
          </EmptyCell>
        </tr>
      );
    }
    if (ranks.length === 0) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            マイレージステージがありません
          </EmptyCell>
        </tr>
      );
    }
    return ranks.map((rank) => (
      <ClickableRow key={rank.mileage_id} onClick={() => setEditTarget(rank)}>
        <td className="table-cell-visible">
          <CardThumbnail $src={rank.image} />
        </td>
        <td className="bodytext-13">{rank.rank_name_jp || "—"}</td>
        <td className="bodytext-13">{rank.rank_name_en || "—"}</td>
        <td className="bodytext-13 u-text-right">
          {rank.point.toLocaleString()} マイル
        </td>
        <td className="bodytext-13 u-text-right">{rank.usage_fee}%</td>
        <td className="bodytext-13 u-text-right">
          {rank.ratio_percent}%
          <span className="bodytext-11 u-text-light-gray">
            （{rank.artist_count}/{artistTotal}人）
          </span>
        </td>
        <td
          className="table-cell-visible"
          style={{ textAlign: "center" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* デフォルトステージは削除不可（BE でも 409 ガード） */}
          {!rank.is_default &&
            (isMinimumReached ? (
              <ToolTip description={`ステージは最低${minRanks}件必要です`}>
                <span className="material-symbols-rounded u-fontsize-18 u-text-light-gray">
                  delete
                </span>
              </ToolTip>
            ) : (
              <IconButton
                icon="delete"
                size={18}
                title="ステージを削除"
                ariaLabel={`${rank.rank_name_jp || rank.rank_name_en} を削除`}
                onClick={() => setDeleteTarget(rank)}
              />
            ))}
        </td>
      </ClickableRow>
    ));
  };

  return (
    <PageWrapper>
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="MILEAGE" />
        <HelpHint description={"マイレージステージ（ランク）の定義を管理します。ボーダーライン・利用料率・カード画像を編集できます。"} />
      </div>

      <div>
        {isRankLimitReached ? (
          <ToolTip description={`ステージは最大${maxRanks}件までです`}>
            <span>
              <Button size="small" leftIcon="add" disabled>
                新規ステージを追加
              </Button>
            </span>
          </ToolTip>
        ) : (
          <Button
            size="small"
            leftIcon="add"
            onClick={() => setIsCreating(true)}
          >
            新規ステージを追加
          </Button>
        )}
      </div>

      <Table columns={columns} minWidth={760}>
        {renderRows()}
      </Table>

      {/* 作成・編集モーダル */}
      {(isCreating || editTarget) && (
        <MileageRankModal
          rank={editTarget}
          onClose={() => {
            setIsCreating(false);
            setEditTarget(null);
          }}
        />
      )}

      {/* 削除確認 */}
      {deleteTarget && (
        <ConfirmModal
          message={
            `ステージ「${deleteTarget.rank_name_jp || deleteTarget.rank_name_en}」を削除しますか？\n` +
            "この操作は取り消せません。"
          }
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PageWrapper>
  );
};

export default AdminMileageRanks;
